import { useEffect, useRef } from "react";
import { buzz, useChime } from "../hooks/useDevice";
import type { PlayerView } from "../protocol";
import { nudgeFor, pickScreen, type Nudge, type ScreenId } from "../state/selectors";
import { useGame } from "../state/store";
import { DawnScreen, DayScreen } from "./DayScreens";
import { GameOverScreen } from "./GameOverScreen";
import { LobbyScreen } from "./LobbyScreen";
import { NightInfoScreen, NightPromptScreen, NightSleepingScreen } from "./NightScreens";
import { RoleRevealScreen } from "./RoleRevealScreen";
import { ExecutionScreen, NominationScreen, VoteTurnScreen } from "./VotingScreens";

const SCREENS: Record<ScreenId, (props: { view: PlayerView }) => JSX.Element | null> = {
  lobby: LobbyScreen,
  role_reveal: RoleRevealScreen,
  night_sleep: NightSleepingScreen,
  night_prompt: NightPromptScreen,
  night_info: NightInfoScreen,
  dawn: DawnScreen,
  day: DayScreen,
  nomination: NominationScreen,
  vote_turn: VoteTurnScreen,
  execution: ExecutionScreen,
  game_over: GameOverScreen,
};

export function GameScreen({ view }: { view: PlayerView }) {
  const dawnSeenDay = useGame((s) => s.ui.dawnSeenDay);
  const sheetOpen = useGame((s) => s.ui.sheetSeatId !== null);
  const setUi = useGame((s) => s.setUi);
  const screen = pickScreen(view, { dawnSeenDay });
  useNudges(nudgeFor(screen, view));
  useEffect(() => {
    // a seat sheet left open when a nomination starts must not pop back up afterwards
    if (screen !== "day" && sheetOpen) setUi({ sheetSeatId: null });
  }, [screen, sheetOpen, setUi]);
  const Screen = SCREENS[screen];
  // data-screen names the screen for the end-to-end tests; display: contents keeps the layout
  return (
    <div data-screen={screen} style={{ display: "contents" }}>
      <Screen view={view} />
    </div>
  );
}

// one pulse to look at the phone, two when it is your move. A night wake is always one, choice or
// not: the table can hear a buzz, and must not hear which kind of wake it is.
const PULSES: Record<Nudge, number> = {
  role: 1,
  dawn: 1,
  game_over: 1,
  on_trial: 2,
  vote: 2,
  died: 2,
  wake: 1,
};

/** Buzz and chime once as each moment arrives that needs this phone (see nudgeFor). */
function useNudges(nudge: Nudge | null) {
  const chime = useChime();
  const previous = useRef<Nudge | null>(null);
  useEffect(() => {
    if (nudge && nudge !== previous.current) {
      const atNight = nudge === "wake";
      buzz(PULSES[nudge], atNight);
      chime(atNight);
    }
    previous.current = nudge;
  }, [nudge, chime]);
}
